import { map } from "rxjs/operators";
import { Transaction } from "@models/transaction";
import { combineLatest, Observable } from "rxjs";
import { Helper } from "@helpers/functions";
import { Dictionary } from "@helpers/types";
import { Trade } from "@models";
import { Injectable } from "@angular/core";
import { AccountService } from "@modules/account/services/account.service";
import { UserService } from "@main-services/user.service";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";

export interface AssetGroupTransactions {
	filter: Filter;
	txDates: string[]; // keys for below dictionary
	transactions: Dictionary<(Transaction | Trade)[]>;
}

interface Filter {
	name: string;
	data: (Transaction | Trade)[];
	values: string[];
	hidden: boolean;
}

@Injectable()
export class ActivitiesFacade {
	constructor(
		private readonly accountService: AccountService,
		private readonly userService: UserService,
		private readonly launchDarklyService: LaunchDarklyService
	) {}

	private areLeverageActive$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.LEVERAGE);
	private areSecurityTokenActive$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.SECURITY_TOKENS);
	public transactionDateFormat = "LL"; // Check Moment.js documentation for more formats examples

	private readonly fiats: string[] = [
		"fiat_wallet_transactions",
		"pending_fiat_wallet_transactions",
		"fiat_earn_wallet_transactions",
		"fiat_earn_pending_wallet_transactions",
		"fiat_earn_trades"
	];

	private readonly cryptos: string[] = [
		"cryptocoin_wallet_transactions",
		"cryptocoin_pending_wallet_transactions",
		"cryptocoin_trades"
	];
	private readonly leveragedTokens: string[] = [
		"leveraged_token_wallet_transactions",
		"leveraged_token_pending_wallet_transactions",
		"leveraged_token_trades"
	];
	private readonly metals: string[] = [
		"commodity_wallet_transactions",
		"commodity_pending_wallet_transactions",
		"commodity_trades"
	];
	private readonly indices: string[] = ["user_index_actions"];
	private readonly stocks: string[] = [
		"stock_wallet_transactions",
		"stock_pending_wallet_transactions",
		"stock_trades"
	];
	private readonly etfs: string[] = ["etf_wallet_transactions", "etf_pending_wallet_transactions", "etf_trades"];
	private readonly etcs: string[] = ["etc_wallet_transactions", "etc_pending_wallet_transactions", "etc_trades"];
	private readonly securityTokens: string[] = [
		"security_token_wallet_transactions",
		"security_token_pending_wallet_transactions",
		"security_token_trades"
	];

	private filters: Filter[] = [
		{
			name: "currency_filter_view_all",
			data: [],
			values: [
				...this.fiats,
				...this.cryptos,
				...this.leveragedTokens,
				...this.indices,
				...this.stocks,
				...this.etfs,
				...this.metals,
				...this.etcs,
				...this.securityTokens
			],
			hidden: true
		},
		{
			name: "currency_filter_currencies",
			data: [],
			values: this.fiats,
			hidden: true
		},
		{
			name: "search_crypto",
			data: [],
			values: this.cryptos,
			hidden: true
		},
		{
			name: "currency_filter_leveraged_tokens",
			data: [],
			values: this.leveragedTokens,
			hidden: true
		},
		{
			name: "currency_filter_crypto_indices",
			data: [],
			values: this.indices,
			hidden: true
		},
		{
			name: "currency_filter_stocks",
			data: [],
			values: this.stocks,
			hidden: true
		},
		{
			name: "currency_filter_etfs",
			data: [],
			values: this.etfs,
			hidden: true
		},
		{
			name: "currency_filter_etcs",
			data: [],
			values: this.etcs,
			hidden: true
		},
		{
			name: "currency_filter_metals",
			data: [],
			values: this.metals,
			hidden: true
		},
		{
			name: "currency_filter_security",
			data: [],
			values: this.securityTokens,
			hidden: true
		}
	];

	private activities = this.userService.activity().pipe(
		map((activities) =>
			this.filters.map((filter: Filter) => {
				filter.data = filter.values
					.map((value) => activities[value] as Transaction[])
					.filter((value) => !!value)
					.reduce((a, b) => [...a, ...b], [])
					.filter((value) => !!value)
					.filter(
						(item, index, array) =>
							index === array.indexOf(array.find((curr) => curr.getId() === item.getId()))
					)
					.sort((a, b) => {
						const timeA: number = a.getTime().getUnix();
						const timeB: number = b.getTime().getUnix();
						if (timeA === timeB) {
							return a.isOutgoing() ? 1 : -1;
						}
						return timeB - timeA;
					})
					.slice(0, 5);
				filter.hidden = filter.data.length < 1;
				return filter;
			})
		)
	);

	private filteredActivities$ = combineLatest([
		this.areLeverageActive$,
		this.areSecurityTokenActive$,
		this.activities
	]).pipe(
		map(([activeL, activeSecToken, filters]) => {
			if (!activeL) {
				return filters.filter((a) => a.values !== this.leveragedTokens);
			}
			if (!activeSecToken) {
				return filters.filter((a) => a.values !== this.securityTokens);
			}
			return filters;
		})
	);

	public loadActivities(): void {
		this.userService.getActivities();
	}

	public assetGroupTransactions$: Observable<AssetGroupTransactions[]> = this.filteredActivities$.pipe(
		map((activities: Filter[]) => {
			const groups: AssetGroupTransactions[] = [];

			activities.forEach((filter) => {
				// date[1] to txs[many] map
				const txMap = Helper.getMap(filter?.data, this.transactionDateFormat);
				const txDates = [];
				const transactionsPerDate: Dictionary<(Transaction | Trade)[]> = {};

				for (const date of txMap.keys()) {
					txDates.push(date);
					transactionsPerDate[date] = txMap.get(date);
				}

				if (txDates.length > 0) {
					groups.push({
						filter: filter,
						txDates: txDates,
						transactions: transactionsPerDate
					});
				}
			});

			return groups;
		})
	);
}
